'use client'

import Link from 'next/link'
import { ProgressiveImage } from '@/ProgressiveImage'
import { tracking } from '@/tracking'
import type { CategoryListingProduct } from './helpers'

type CategoryTopSellersProps = {
  products: CategoryListingProduct[]
  /** Display name for the category — used when `heading` is not passed */
  categoryName: string
  /** Full H2 text; overrides the default “Top Selling {categoryName}” */
  heading?: string
}

export function CategoryTopSellers({
  products,
  categoryName,
  heading: headingOverride,
}: CategoryTopSellersProps) {
  if (products.length === 0) {
    return null
  }

  const heading =
    headingOverride?.trim() ||
    (categoryName.trim().length > 0
      ? `Top Selling ${categoryName.trim()}`
      : 'Top Selling')

  return (
    <section className="mt-8 rounded bg-[#fafafa] p-4">
      <h2 className="mb-3 text-xl border-b border-gray-200 pb-2 font-bold text-gray-900">
        {heading}
      </h2>
      <ul className="flex flex-col gap-3">
        {products.map((product, index) => {
          return (
            <li key={product.id}>
              <Link
                href={product.url || '#'}
                onClick={() =>
                  tracking.track('Clicked category top seller', {
                    Category: categoryName,
                    Heading: heading,
                    'Product id': product.id,
                    'Product name': product.name,
                    Position: index + 1,
                    Url: product.url || '#',
                  })
                }
                className="group flex gap-3 rounded-md p-1 transition-colors hover:bg-white"
              >
                <div className="relative h-[72px] w-[46px] shrink-0 overflow-hidden rounded bg-gray-100">
                  {product.thumbnails?.default ? (
                    <ProgressiveImage
                      imageUrl={`${product.thumbnails.default}?w=92&dpr=2`}
                      alt={product.name || 'Product'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full bg-gray-200" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-base font-semibold leading-snug text-gray-900 group-hover:text-vi-red group-hover:underline">
                    {product.name}
                  </p>
                  {product.creators[0]?.name ? (
                    <p className="mt-0.5 text-sm text-gray-500">
                      {product.creators[0].name}
                    </p>
                  ) : null}
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
